import React, { useState, useEffect } from 'react';
import {
  Bars3Icon,
  XMarkIcon,
  PlusIcon,
  ChatBubbleLeftIcon,
  PencilIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  SunIcon,
  MoonIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';
import Modal from './Modal';

const Sidebar = ({
  isOpen,
  onToggle,
  conversations,
  currentConversationId,
  onSelectConversation,
  onNewConversation,
  onRenameConversation,
  onDuplicateConversation,
  onDeleteConversation,
  onClearHistory,
  darkMode,
  onToggleDarkMode,
  onLogout
}) => {
  const [renameModal, setRenameModal] = useState({ isOpen: false, id: null, name: '' });
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, id: null, name: '' });
  const [clearHistoryModal, setClearHistoryModal] = useState(false);
  const [newName, setNewName] = useState('');

  const handleRename = (id, currentName) => {
    setRenameModal({ isOpen: true, id, name: currentName });
    setNewName(currentName);
  };

  const handleSaveRename = () => {
    if (newName.trim() && renameModal.id) {
      // Update both name and aiTitle when manually renamed
      onRenameConversation(renameModal.id, newName.trim());
    }
    setRenameModal({ isOpen: false, id: null, name: '' });
    setNewName('');
  };

  const handleCancelRename = () => {
    setRenameModal({ isOpen: false, id: null, name: '' });
    setNewName('');
  };

  const handleDeleteClick = (id, name) => {
    setDeleteModal({ isOpen: true, id, name });
  };

  const handleConfirmDelete = () => {
    if (deleteModal.id) {
      onDeleteConversation(deleteModal.id);
    }
    setDeleteModal({ isOpen: false, id: null, name: '' });
  };

  const handleCancelDelete = () => {
    setDeleteModal({ isOpen: false, id: null, name: '' });
  };

  const handleClearHistoryClick = () => {
    setClearHistoryModal(true);
  };

  const handleConfirmClearHistory = () => {
    if (onClearHistory) {
      onClearHistory();
    }
    setClearHistoryModal(false);
  };

  const handleCancelClearHistory = () => {
    setClearHistoryModal(false);
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    return date.toLocaleDateString();
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
          onClick={onToggle}
        />
      )}
      
      {/* Sidebar */}
      <div className={`
        fixed top-0 left-0 h-full w-80 bg-white/90 dark:bg-[#1a1b26]/90
        backdrop-blur-xl border-r border-gray-200/50 dark:border-[#414868]/50
        transform transition-transform duration-300 ease-out z-50
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-[#c0caf5]">
              Conversations
            </h2>
            <button
              onClick={onToggle}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-[#414868]
                       transition-colors"
            >
              <XMarkIcon className="h-5 w-5 text-gray-600 dark:text-[#c0caf5]" />
            </button>
          </div>

          {/* New Conversation Button */}
          <div className="p-4">
            <button
              onClick={onNewConversation}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl
                       bg-gradient-to-r from-blue-500 to-blue-600 text-white
                       hover:from-blue-600 hover:to-blue-700 transition-all duration-200
                       shadow-sm hover:shadow-md modern-button"
            >
              <PlusIcon className="h-5 w-5" />
              <span className="font-medium">New Conversation</span>
            </button>
          </div>

          {/* Conversations List */}
          <div className="flex-1 overflow-y-auto px-4 space-y-2">
            {conversations.map((conversation) => (
              <div
                key={conversation.id}
                className={`
                  group relative p-3 rounded-xl cursor-pointer transition-all duration-200
                  ${currentConversationId === conversation.id
                    ? 'bg-blue-50 dark:bg-[#7aa2f7]/20 border border-blue-200 dark:border-[#7aa2f7]/50'
                    : 'hover:bg-gray-50 dark:hover:bg-[#24283b]/50'
                  }
                `}
                onClick={() => onSelectConversation(conversation.id)}
              >
                <div className="flex items-start gap-3">
                  <ChatBubbleLeftIcon className="h-5 w-5 text-gray-400 dark:text-[#9aa5ce] mt-0.5 flex-shrink-0" />
                  
                  <div className="flex-1 min-w-0">
                    {/* AI-generated title or fallback */}
                    <h3 className="font-medium text-gray-900 dark:text-[#c0caf5] truncate">
                      {conversation.aiTitle || conversation.name}
                    </h3>
                    {/* Show loading indicator if AI title is being generated */}
                    {!conversation.aiTitle && (
                      <div className="flex items-center gap-1 mt-1">
                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                        <span className="text-xs text-blue-500 dark:text-[#7aa2f7]">Generating title...</span>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 dark:text-[#9aa5ce] mt-1">
                      {formatDate(conversation.updatedAt)} • {conversation.messages.length} messages
                    </p>
                    
                    {/* Action Buttons - moved below title */}
                    <div className="flex items-center gap-1 mt-2 opacity-75 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRename(conversation.id, conversation.aiTitle || conversation.name);
                        }}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50
                                 dark:hover:text-[#7aa2f7] dark:hover:bg-[#7aa2f7]/20 rounded-lg transition-colors"
                        title="Rename"
                      >
                        <PencilIcon className="h-3 w-3" />
                      </button>
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDuplicateConversation(conversation.id);
                        }}
                        className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50
                                 dark:hover:text-[#9ece6a] dark:hover:bg-[#9ece6a]/20 rounded-lg transition-colors"
                        title="Duplicate"
                      >
                        <DocumentDuplicateIcon className="h-3 w-3" />
                      </button>
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(conversation.id, conversation.aiTitle || conversation.name);
                        }}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50
                                 dark:hover:text-[#f7768e] dark:hover:bg-[#f7768e]/20 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <TrashIcon className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            {conversations.length === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-[#9aa5ce]">
                <ChatBubbleLeftIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No conversations yet</p>
                <p className="text-sm">Start a new conversation to get started</p>
              </div>
            )}
          </div>

          {/* Mobile-only controls */}
          <div className="lg:hidden p-4 space-y-3 border-t border-gray-200/50 dark:border-[#414868]/50">
            {/* Model badge */}
            <div className="flex justify-center">
              <span className="px-3 py-1 text-xs font-bold rounded-full font-mono
                           bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg">
                GPT-5.6 Sol
              </span>
            </div>

            {/* Control buttons */}
            <div className="flex items-center justify-center gap-3">
              {/* Logout Button */}
              <button
                onClick={onLogout}
                className="flex items-center justify-center p-3 rounded-xl bg-red-100 dark:bg-red-900/30
                         hover:bg-red-200 dark:hover:bg-red-900/50 transition-all duration-200
                         shadow-lg hover:shadow-xl text-red-600 dark:text-red-400"
                title="Sign Out"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
              </button>

              {/* Dark Mode Toggle */}
              <button
                onClick={onToggleDarkMode}
                className="flex items-center justify-center p-3 rounded-xl bg-gray-100 dark:bg-[#414868]
                         hover:bg-gray-200 dark:hover:bg-[#565f89] transition-all duration-200
                         shadow-lg hover:shadow-xl"
                aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
              >
                {darkMode ? (
                  <SunIcon className="h-5 w-5 text-[#e0af68]" />
                ) : (
                  <MoonIcon className="h-5 w-5 text-gray-600" />
                )}
              </button>
            </div>
          </div>

          {/* Clear History Button */}
          {conversations.length > 0 && (
            <div className="p-4">
              <button
                onClick={handleClearHistoryClick}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl
                         bg-red-50 dark:bg-[#f7768e]/20 text-red-600 dark:text-[#f7768e]
                         hover:bg-red-100 dark:hover:bg-[#f7768e]/30 transition-all duration-200
                         border border-red-200 dark:border-[#f7768e]/50
                         font-medium text-sm"
              >
                <TrashIcon className="h-4 w-4" />
                Clear Conversation History
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Rename Modal */}
      <Modal
        isOpen={renameModal.isOpen}
        onClose={handleCancelRename}
        title="Rename Conversation"
        size="sm"
      >
        <div className="space-y-4">
          <div>
            <p className="text-sm text-gray-600 dark:text-[#9aa5ce] mb-3">
              Give your conversation a custom name to make it easier to find later. This will replace the AI-generated title.
            </p>
            <label className="block text-sm font-medium text-gray-700 dark:text-[#c0caf5] mb-2">
              Conversation Name
            </label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveRename();
                if (e.key === 'Escape') handleCancelRename();
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-[#414868]
                       rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-[#7aa2f7]
                       bg-white dark:bg-[#24283b] text-gray-900 dark:text-[#c0caf5]"
              placeholder="Enter conversation name..."
              autoFocus
            />
          </div>
          
          <div className="flex justify-end gap-3">
            <button
              onClick={handleCancelRename}
              className="px-4 py-2 text-gray-600 dark:text-[#9aa5ce] hover:text-gray-800
                       dark:hover:text-[#c0caf5] hover:bg-gray-100 dark:hover:bg-[#414868]
                       rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveRename}
              disabled={!newName.trim()}
              className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700
                       disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed
                       text-white rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
            >
              Save Changes
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={handleCancelDelete}
        title="Delete Conversation"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-500 dark:text-[#f7768e] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-gray-900 dark:text-[#c0caf5] font-medium">
                Are you sure you want to delete "{deleteModal.name}"?
              </p>
              <p className="text-sm text-gray-500 dark:text-[#9aa5ce] mt-1">
                This action cannot be undone. All messages in this conversation will be permanently deleted from your device.
              </p>
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <button
              onClick={handleCancelDelete}
              className="px-4 py-2 text-gray-600 dark:text-[#9aa5ce] hover:text-gray-800
                       dark:hover:text-[#c0caf5] hover:bg-gray-100 dark:hover:bg-[#414868]
                       rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDelete}
              className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700
                       text-white rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
            >
              Delete Conversation
            </button>
          </div>
        </div>
      </Modal>

      {/* Clear History Confirmation Modal */}
      <Modal
        isOpen={clearHistoryModal}
        onClose={handleCancelClearHistory}
        title="Clear All Conversations"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-500 dark:text-[#f7768e] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-gray-900 dark:text-[#c0caf5] font-medium">
                Are you sure you want to clear all conversation history?
              </p>
              <p className="text-sm text-gray-500 dark:text-[#9aa5ce] mt-1">
                This will permanently delete all {conversations.length} conversations from your device and cannot be undone.
                Consider exporting important conversations before proceeding.
              </p>
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <button
              onClick={handleCancelClearHistory}
              className="px-4 py-2 text-gray-600 dark:text-[#9aa5ce] hover:text-gray-800
                       dark:hover:text-[#c0caf5] hover:bg-gray-100 dark:hover:bg-[#414868]
                       rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmClearHistory}
              className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700
                       text-white rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
            >
              Clear All History
            </button>
          </div>
        </div>
      </Modal>

    </>
  );
};

export default Sidebar;
